import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import loadImage from "blueimp-load-image";

/**
 * ### 画像圧縮クラス
 */
export default class ImageFileCompression {
  /**
   * ### 画像を圧縮する
   *
   * @param file 圧縮対象ファイル
   * @param mimeType 圧縮した画像の出力mimetype
   * @param maxSize 圧縮した画像の最大容量(byte)
   * @param maxWidth 圧縮した画像最大横幅
   * @param maxHeight 圧縮した画像最大縦幅
   */
  public static async imageCompression(file: File, mimeType: string, maxSize: number, maxWidth: number, maxHeight: number): Promise<File | undefined> {
    // 画像ファイル以外の場合は処理を中断して例外を投げる
    if (!file.type.startsWith("image/")) {
      throw new Error("画像ではありません。");
    }

    const base64Image: string = await FileReaderWrapper.readAsDataURL(file);
    let image: HTMLImageElement;
    try {
      image = await this.imageLoading(base64Image);
    } catch (e) {
      // 画像として読み込めない場合は、画像ではないファイルと見なして例外を投げる
      // これにより、拡張子だけ画像にしたテキストファイルなどを弾くことができる
      throw new Error("画像形式ではないか、破損しています。");
    }

    // EXIFを読み込んで回転向きを取得し、この後作るcanvasのサイズ等を判定する
    const orientation: number = await this.getOrientation(file);

    // 圧縮対象の画像からcanvas要素を作る
    const canvas: HTMLCanvasElement | undefined = this.createCanvasElement(image, orientation, maxWidth, maxHeight);
    // ありえないが、canvasがundefinedなら圧縮処理をせずに返却する
    if (canvas == undefined) {
      return undefined;
    }

    // 圧縮実施
    const compressionFile = this.doCompression(file, canvas, mimeType, maxSize);

    return compressionFile;
  }

  /**
   * 画像の読込み
   *
   * Image要素はonloadに読込み後のfunctionを登録する仕様になっている
   * Promiseを返すようにラップする
   */
  private static async imageLoading(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = (e) => reject(e);
      image.src = url;
    });
  }

  /**
   * ### EXIF情報のOrientationを返す
   */
  public static async getOrientation(file: File): Promise<number> {
    try {
      const metaData: loadImage.MetaData = await loadImage.parseMetaData(file);
      if (metaData.exif) {
        const tagValue: loadImage.ExifTagValue = metaData.exif.get("Orientation");
        if (isNaN(Number(tagValue))) {
          return 1;
        } else {
          return Number(tagValue);
        }
      }
      return 1;
    } catch (e) {
      // 例外の場合、1（回転無し）を返す
      return 1;
    }
  }

  /**
   * ### Canvas要素を作る
   *
   * - EXIFを考慮して必要に応じて回転させる
   *
   * @param image Canvas作成対象の画像
   * @param orientation EXIFのOrientation
   * @param maxWidth 画像の最大サイズ(横)
   * @param maxHeight 画像の最大サイズ(縦)
   */
  private static createCanvasElement(image: HTMLImageElement, orientation: number, maxWidth: number, maxHeight: number): HTMLCanvasElement | undefined {
    // canvasのコンテキストを取得する、ありえないが取得出来なかった場合はundefinedを返す
    const canvas: HTMLCanvasElement = document.createElement("canvas");
    const context: CanvasRenderingContext2D | null = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    // 最大サイズ以上の画像の場合、縮小する必要があるので縦横サイズを計算する
    let size: { width: number; height: number };
    if (-1 < [5, 6, 7, 8].indexOf(orientation)) {
      size = ImageFileCompression.calculateCanvasSize(image.height, image.width, maxHeight, maxWidth);
    } else {
      size = ImageFileCompression.calculateCanvasSize(image.width, image.height, maxWidth, maxHeight);
    }
    canvas.width = size.width;
    canvas.height = size.height;

    // EXIFのorientationによっては90度回転が必要なので、その場合は縦・横のサイズを入れ替える
    // EXIFのorientationに応じて画像を回転させる
    // transform()は実行しなくても回転しているようなので、コメントアウトしておく
    switch (orientation) {
      case 2:
        //context.transform(-1, 0, 0, 1, image.width, 0);
        context.drawImage(image, 0, 0, size.width, size.height);
        break;
      case 3:
        //context.transform(-1, 0, 0, -1, image.width, image.height);
        context.drawImage(image, 0, 0, size.width, size.height);
        break;
      case 4:
        //context.transform(1, 0, 0, -1, 0, image.height);
        context.drawImage(image, 0, 0, size.width, size.height);
        break;
      case 5:
        //context.transform(0, 1, 1, 0, 0, 0);
        canvas.width = size.height;
        canvas.height = size.width;
        context.drawImage(image, 0, 0, size.height, size.width);
        break;
      case 6:
        //context.transform(0, 1, -1, 0, image.height, 0);
        canvas.width = size.height;
        canvas.height = size.width;
        context.drawImage(image, 0, 0, size.height, size.width);
        break;
      case 7:
        //context.transform(0, -1, -1, 0, image.height, image.width);
        canvas.width = size.height;
        canvas.height = size.width;
        context.drawImage(image, 0, 0, size.height, size.width);
        break;
      case 8:
        //context.transform(0, -1, 1, 0, 0, image.width);
        canvas.width = size.height;
        canvas.height = size.width;
        context.drawImage(image, 0, 0, size.height, size.width);
        break;
      default:
        context.drawImage(image, 0, 0, size.width, size.height);
    }

    return canvas;
  }

  /**
   * ### canvasのサイズを計算する
   *
   * @param canvas canvas要素
   * @param image 画像のloadが完了したimage要素
   * @param maxWidth 画像最大横幅
   * @param maxHeight 画像最大縦幅
   */
  public static calculateCanvasSize(imageWidth: number, imageHeight: number, maxWidth: number, maxHeight: number): { width: number; height: number } {
    let width: number = 0;
    let height: number = 0;

    // 最大値より大きい場合は同じ比率で縮小する
    if (maxWidth < imageWidth || maxHeight < imageHeight) {
      if (imageWidth < imageHeight) {
        width = (imageWidth * maxHeight) / imageHeight;
        height = maxHeight;
      } else {
        width = maxWidth;
        height = (imageHeight * maxWidth) / imageWidth;
      }
    } else {
      width = imageWidth;
      height = imageHeight;
    }

    return { width, height };
  }

  /**
   * ### 圧縮本処理
   *
   * @param file 圧縮対象のファイル
   * @param canvas キャンバスオブジェクト
   * @param mimeType MimeType
   * @param maxSize ファイルの最大サイズ
   */
  public static doCompression(file: File, canvas: HTMLCanvasElement, mimeType: string, maxSize: number): File {
    // ファイルサイズを見て、圧縮が必要か判断する
    if (maxSize < file.size) {
      // 圧縮率0.75で圧縮して最大容量を下回ればそれを使用する
      let base64CanvasImage: string = canvas.toDataURL(mimeType);
      let convertedFile: File = ImageFileCompression.convertBase64ToFile(base64CanvasImage, file);

      if (convertedFile.size < maxSize) {
        return convertedFile;
      }

      // 圧縮率0.5で圧縮して最大容量を下回ればそれを使用する
      base64CanvasImage = canvas.toDataURL(mimeType, 0.5);
      convertedFile = ImageFileCompression.convertBase64ToFile(base64CanvasImage, file);

      if (convertedFile.size < maxSize) {
        return convertedFile;
      }

      // 0.5でもダメな場合はほぼ確実な圧縮率で圧縮する
      const ratio: number = maxSize / file.size;
      base64CanvasImage = canvas.toDataURL(mimeType, ratio);
      convertedFile = ImageFileCompression.convertBase64ToFile(base64CanvasImage, file);

      return convertedFile;
    } else {
      // 元ファイルが1MB以下の場合に、canvas経由でリサイズした後に圧縮すると1MBを超えてしまう
      // デフォルトの圧縮率0.92らしいが、この数値が怖いので圧縮率は少し上げておく
      const base64CanvasImage: string = canvas.toDataURL(mimeType, 0.75);
      const convertedFile: File = ImageFileCompression.convertBase64ToFile(base64CanvasImage, file);

      return convertedFile;
    }
  }

  /**
   * ### Base64文字列からFileインスタンスに変換する
   *
   * @param base64File Base64文字列
   */
  public static convertBase64ToFile(base64File: string, file: File): File {
    // Base64文字列からコンテンツタイプを取得する
    const contentType = base64File.toString().slice(base64File.indexOf(":") + 1, base64File.indexOf(";"));

    // Base64文字列をバッファに変換して、バッファからFileインスタンスを作る
    const binary: string = atob(base64File.replace(/^.*,/, ""));
    const buffer: Uint8Array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      buffer[i] = binary.charCodeAt(i);
    }
    const convertedFile: File = new File([buffer.buffer] as any, file.name, { type: contentType });

    return convertedFile;
  }
}
